import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Mail, Loader2, Receipt, Users, HandCoins, Eye, ChevronUp } from "lucide-react";
import { api, errMsg, downloadFile, money, fmtDate } from "@/lib/api";
import { PageHeader, Card, Empty, StatCard } from "@/components/admin/ui";
import { StatementPreview } from "@/components/admin/BillingPreview";

const prevMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function Billing() {
  const [month, setMonth] = useState(prevMonth());
  const [d, setD] = useState<any>(null);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const latest = React.useRef("");
  const load = useCallback(() => {
    latest.current = month;
    return api.get("/admin/billing", { params: { month } }).then((r) => { if (latest.current === month) setD(r.data); }).catch((e) => toast.error(errMsg(e)));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/billing/${month}/email`);
      toast.success(`Reports emailed to ${data.to}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSending(false);
  };

  return (
    <div>
      <PageHeader
        title="Billing & partner payout"
        subtitle="Completed backgrounds by month. Reports are emailed to accounting automatically on the 1st."
        actions={
          <>
            <input type="month" className="field-input h-9 w-44 text-sm" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="billing-month-input" />
            <button className="btn-secondary btn-sm" onClick={() => downloadFile(`/admin/billing/${month}/organizations.pdf`, `billing-${month}.pdf`)} data-testid="billing-download-all-button"><Download className="h-4 w-4" /> All org reports</button>
            <button className="btn-secondary btn-sm" onClick={() => downloadFile(`/admin/billing/${month}/master.pdf`, `partner-master-${month}.pdf`)} data-testid="billing-download-master-button"><Download className="h-4 w-4" /> Partner master list</button>
            <button className="btn-primary btn-sm" disabled={sending} onClick={send} data-testid="billing-email-button">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email to accounting now</button>
          </>
        }
      />
      {d && (
        <>
          <div className="mb-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard testId="billing-stat-completed" label={`Completed in ${d.label}`} value={d.master.count} icon={<Users className="h-5 w-5" />} />
            <StatCard testId="billing-stat-invoice" label="To invoice organizations" value={money(d.billable_total)} icon={<Receipt className="h-5 w-5" />} tone="amber" hint={`Manual entries ${money(d.manual_fee)} · code applicants $0 · NY +${money(d.ny_adjustment)} · plus additional search fees`} />
            <StatCard testId="billing-stat-ny" label="NY add-ons (included)" value={money(d.ny_total)} icon={<Receipt className="h-5 w-5" />} tone="red" hint={`${d.ny_count} applicant(s) × ${money(d.ny_adjustment)}, on each organization's bill`} />
            <StatCard testId="billing-stat-partner" label={`${d.partner_name} payout`} value={money(d.master.grand_total)} icon={<HandCoins className="h-5 w-5" />} tone="green" hint={`${d.master.count} × ${money(d.partner_fee)}`} />
          </div>
          <Card title="Organization billing reports" testId="billing-orgs-card">
            {d.organizations.length ? (
              <div className="overflow-x-auto">
                <table className="tbl" data-testid="billing-orgs-table">
                  <thead><tr><th>Organization</th><th>Ref No</th><th>Applicants</th><th>With code</th><th>Manual</th><th>Amount due</th><th /></tr></thead>
                  <tbody>
                    {d.organizations.map((o: any) => (
                      <React.Fragment key={o.organization.id}>
                      <tr data-testid={`billing-org-row-${o.organization.id}`}>
                        <td><Link to={`/admin/organizations/${o.organization.id}`} className="font-semibold text-[#1B365D] hover:underline">{o.organization.name}</Link>{o.organization.parent_org_id && <span className="ml-2 rounded-full bg-[#E0F2FE] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0369A1]" data-testid={`billing-parish-badge-${o.organization.id}`}>Parish</span>}<p className="text-xs text-[#94A3B8]">{o.organization.contact_name} · {o.organization.email}</p></td>
                        <td className="font-mono text-xs">{o.organization.reference_no || "—"}</td>
                        <td className="font-mono">{o.total_applicants}</td>
                        <td className="font-mono">{o.rows.filter((r: any) => r.type === "PreScreen").length}</td>
                        <td className="font-mono">{o.rows.filter((r: any) => r.type !== "PreScreen").length}</td>
                        <td className="font-mono font-semibold">{money(o.total_due)}</td>
                        <td className="whitespace-nowrap text-right">
                          <button className="btn-secondary btn-sm mr-2" onClick={() => setOpen(open === o.organization.id ? null : o.organization.id)} data-testid={`billing-org-preview-${o.organization.id}`}>{open === o.organization.id ? <ChevronUp className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {open === o.organization.id ? "Hide" : "Preview"}</button>
                          <button className="btn-secondary btn-sm" onClick={() => downloadFile(`/admin/billing/${month}/organizations.pdf?org_id=${o.organization.id}`, `billing-${month}-${o.organization.name}.pdf`)} data-testid={`billing-org-download-${o.organization.id}`}><Download className="h-4 w-4" /> PDF</button>
                        </td>
                      </tr>
                      {open === o.organization.id && <tr><td colSpan={7} className="p-0"><StatementPreview o={o} month={month} onChanged={load} /></td></tr>}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty text={`No backgrounds were delivered in ${d.label}.`} />}
          </Card>
          <Card title={`Omitted from billing (${(d.omitted || []).length})`} testId="billing-omitted-card">
            {(d.omitted || []).length ? (
              <div className="overflow-x-auto">
                <table className="tbl" data-testid="billing-omitted-table">
                  <thead><tr><th>Applicant</th><th>Organization</th><th>Delivered</th><th>Reason</th><th>Omitted by</th><th /></tr></thead>
                  <tbody>
                    {d.omitted.map((o: any) => (
                      <tr key={o.id} data-testid={`billing-omitted-row-${o.id}`}>
                        <td><Link to={`/admin/cases/${o.id}`} className="font-semibold text-[#1B365D] hover:underline" data-testid={`billing-omitted-link-${o.id}`}>{o.name || "(no name)"}</Link></td>
                        <td>{o.organization}</td>
                        <td className="font-mono text-xs">{fmtDate(o.delivered_at)}</td>
                        <td className="text-sm">{o.reason || <span className="text-[#94A3B8]">No reason given</span>}</td>
                        <td className="text-xs text-[#64748B]">{o.by || "—"}</td>
                        <td className="text-right"><button className="btn-secondary btn-sm" onClick={() => api.patch(`/admin/cases/${o.id}/billing-omit`, { omit: false }).then(() => { toast.success(`${o.name} is back on the bill`); load(); }).catch((e) => toast.error(errMsg(e)))} data-testid={`billing-omitted-restore-${o.id}`}>Put back on bill</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty text={`No cases were omitted from billing in ${d.label}.`} />}
          </Card>
        </>
      )}
    </div>
  );
}
